<script lang="ts">
  import { Plus } from 'lucide-svelte';
  import { selectEntity } from '$lib/router';
  import { zonesQuery } from '$lib/queries/zones';
  import { roomsQuery } from '$lib/queries/rooms';
  import { loadsQuery } from '$lib/queries/loads';
  import { rcuChannelsQuery } from '$lib/queries/rcu_channels';
  import { dinChannelsQuery } from '$lib/queries/din_channels';
  import { inputDevicesQuery } from '$lib/queries/input_devices';
  import { createRoomMutation } from '$lib/mutations/rooms';
  import { toast } from '$lib/state/toast.svelte';
  import { getErrorMessage } from '$lib/api';

  let { id }: { id: number } = $props();

  const zones = zonesQuery();
  const rooms = roomsQuery();
  const loads = loadsQuery();
  const rcuChannels = rcuChannelsQuery();
  const dinChannels = dinChannelsQuery();
  const devices = inputDevicesQuery();
  const createRoom = createRoomMutation();

  const zone = $derived((zones.data ?? []).find((z) => z.id === id));
  const zoneRooms = $derived((rooms.data ?? []).filter((r) => r.zone === id));

  function roomStats(roomId: number) {
    const roomLoads = (loads.data ?? []).filter((l) => l.room === roomId);
    const assignedLoadIds = new Set([
      ...(rcuChannels.data ?? []).map((c) => c.load),
      ...(dinChannels.data ?? []).map((c) => c.load)
    ]);
    const assigned = roomLoads.filter((l) => assignedLoadIds.has(l.id)).length;
    const coverage = roomLoads.length === 0 ? 0 : Math.round((assigned / roomLoads.length) * 100);
    const keypadCount = (devices.data ?? []).filter((d) => d.location_room === roomId).length;
    return { loadCount: roomLoads.length, coverage, keypadCount };
  }

  function addRoom() {
    const name = prompt('Room name');
    if (!name?.trim()) return;

    createRoom.mutate(
      { zone: id, name: name.trim() },
      {
        onSuccess: (newRoom) => {
          toast.success(`Room "${newRoom.name}" created`);
          selectEntity('room', newRoom.id);
        },
        onError: (error) => toast.error(getErrorMessage(error))
      }
    );
  }
</script>

<div class="p-3">
  <div class="flex items-center justify-between mb-3">
    <div>
      <h2 class="text-base font-medium text-text-primary">{zone?.name ?? `Zone ${id}`}</h2>
      <p class="text-xs text-text-muted">{zoneRooms.length} rooms</p>
    </div>
    <button
      type="button"
      class="btn-primary inline-flex items-center gap-1.5 text-sm"
      onclick={addRoom}
    >
      <Plus class="w-3.5 h-3.5" />
      Add room
    </button>
  </div>

  {#if zoneRooms.length === 0}
    <p class="text-sm text-text-muted py-6 text-center">No rooms in this zone yet.</p>
  {:else}
    <div class="border border-border-default rounded overflow-hidden">
      <table class="w-full text-sm">
        <thead class="bg-bg-rail text-text-muted text-xs">
          <tr class="h-8">
            <th class="text-left px-3 font-medium">Room</th>
            <th class="text-right px-3 font-medium w-24">Loads</th>
            <th class="text-right px-3 font-medium w-32">Coverage</th>
            <th class="text-right px-3 font-medium w-24">Devices</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-border-default">
          {#each zoneRooms as room}
            {@const stats = roomStats(room.id)}
            <tr
              class="h-8 cursor-pointer hover:bg-bg-hover transition-colors"
              onclick={() => selectEntity('room', room.id)}
              onkeydown={(e) => e.key === 'Enter' && selectEntity('room', room.id)}
              tabindex="0"
              role="button"
            >
              <td class="px-3 font-medium text-text-primary">{room.name}</td>
              <td class="px-3 text-right font-mono text-text-secondary">{stats.loadCount}</td>
              <td class="px-3 text-right font-mono text-text-secondary">{stats.coverage}%</td>
              <td class="px-3 text-right font-mono text-text-secondary">{stats.keypadCount}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</div>
